import { useState } from "react";
import { Spinner } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import { useBundles, useMaterials, useMedia, useMeditations } from "@/hooks";
import type { BundleTag } from "@/types";
import {
  Button,
  ControlledDropzone,
  errorToast,
  Input,
  MultiSelect,
  Select,
} from "@/ui";
import { convertToOptions } from "@/utils/selectOptions";

interface AddBundleFormProps {
  onClose: () => void;
}

interface FormValues {
  name: string;
  type: "course" | "training";
  tag: BundleTag;
  image?: File;
  banner?: File;
}

const BUNDLE_TAGS: BundleTag[] = [
  "8 week course",
  "3 session course",
  "12 session course",
  "Teacher Training",
  "Branch Training",
  "Self-paced module",
];

export const AddBundleForm = ({ onClose }: AddBundleFormProps) => {
  const { useUpload } = useMedia();
  const { upload, isUploading } = useUpload();
  const queryClient = useQueryClient();

  const { useAddBundle } = useBundles();
  const { mutateAsync: addBundle, isPending } = useAddBundle();

  const { useAllMaterials } = useMaterials();
  const { data: materialsData } = useAllMaterials();
  const materialOptions = convertToOptions(materialsData?.data ?? []);

  const { useAllMeditations } = useMeditations();
  const { data: meditationsData } = useAllMeditations(1, "");
  const meditationOptions = convertToOptions(meditationsData?.data ?? []);

  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [selectedMeditations, setSelectedMeditations] = useState<string[]>([]);

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    control,
  } = useForm<FormValues>({
    mode: "all",
    defaultValues: {
      name: "",
      type: "course",
      tag: "8 week course",
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const bundleData: Record<string, any> = {
        name: data.name,
        type: data.type,
        tag: data.tag,
        materials: selectedMaterials,
        meditations: selectedMeditations,
      };

      if (data.image) {
        bundleData.image_extension = data.image.name.split(".").pop();
      }

      const newBundle = await addBundle(bundleData);

      if (data.image && newBundle.id) {
        const result = await upload(
          data.image,
          `/bundles/${newBundle.id}/image/upload-url`,
        );
        if (!result.success) {
          errorToast("Failed to upload image. Please try again.");
          return;
        }
      }

      if (data.banner && newBundle.id) {
        const result = await upload(
          data.banner,
          `/bundles/${newBundle.id}/banner/upload-url`,
        );
        if (!result.success) {
          errorToast("Failed to upload banner. Please try again.");
          return;
        }
      }

      await queryClient.invalidateQueries({
        queryKey: ["bundles"],
      });
      onClose();
    } catch (error) {
      errorToast(error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex w-full flex-col gap-y-8 sm:w-[540px]"
    >
      <div className="flex flex-col gap-y-8">
        <Input
          id="name"
          label="Bundle Name"
          placeholder="e.g., Mindfulness for Life"
          {...register("name", { required: "Bundle name is required" })}
          error={errors.name?.message}
          required
        />

        <Select
          id="type"
          label="Type"
          options={[
            { value: "course", label: "Course" },
            { value: "training", label: "Training" },
          ]}
          {...register("type", { required: "Type is required" })}
          error={errors.type?.message}
          required
        />

        <Select
          id="tag"
          label="Tag"
          options={BUNDLE_TAGS.map((tag) => ({ value: tag, label: tag }))}
          {...register("tag", { required: "Tag is required" })}
          error={errors.tag?.message}
          required
        />

        <MultiSelect
          label="Materials (Optional)"
          placeholder="Select materials..."
          options={materialOptions}
          selectedOptions={selectedMaterials}
          onChange={setSelectedMaterials}
        />

        <MultiSelect
          label="Meditations (Optional)"
          placeholder="Select meditations..."
          options={meditationOptions}
          selectedOptions={selectedMeditations}
          onChange={setSelectedMeditations}
        />

        <ControlledDropzone
          name="image"
          control={control}
          label="Image (Optional)"
          placeholder="Upload bundle image"
          accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
        />

        <ControlledDropzone
          name="banner"
          control={control}
          label="Banner (Optional)"
          placeholder="Upload bundle banner"
          accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
        />
      </div>

      <div className="flex justify-center gap-2">
        <Button onClick={onClose} variant="outlined" className="flex-grow">
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-grow"
          disabled={!isValid || isPending || isUploading}
        >
          {isPending || isUploading ? (
            <Spinner className="h-5 w-5 animate-spin" />
          ) : (
            "Create Bundle"
          )}
        </Button>
      </div>
    </form>
  );
};
